"use client";

import { apiCancelOrder, apiGetUserGuessOrder } from "@/api/api";
import EmptyState from "@/components/portfolio/empty-state";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SmartImage } from "@/components/ui/smart-image";
import { cn } from "@/lib/utils";
import { RefreshReason, useUserInfo } from "@/store/user-info";
import { calc, formatMonetaryValue, formatUnitPrice, get } from "@/utils/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface OrderHistorySheetProps {
    open: boolean;
    onOpenChange: ( open: boolean ) => void;
    guessNo: string;
    event: any;
    guess: any;
}

interface OrderBookLevel {
    price: number;
    totalQuantity: number;
    totalAmount: number;
    totalFeeAmount: number;
    totalDealtQuantity: number;
    lastOrderTime: number;
    direction: number; // 1:买 2:卖
    betType: number; // 1:YES 2:NO
    marketPrice: number;
}

interface OrderBookData {
    guessNo: string;
    title: string;
    coverImageUrl: string;
    orders: OrderBookLevel[];
}

// Format timestamp to readable date (MM/DD/YY at HH:mm)
function formatDate( timestamp: number ): string {
    const date = new Date( calc( "timestamp * 1000 | !n", { timestamp } ) as number );
    const month = String( date.getMonth() + 1 ).padStart( 2, "0" );
    const day = String( date.getDate() ).padStart( 2, "0" );
    const year = String( date.getFullYear() ).slice( -2 );
    const hours = String( date.getHours() ).padStart( 2, "0" );
    const minutes = String( date.getMinutes() ).padStart( 2, "0" );
    return `${month}/${day}/${year} at ${hours}:${minutes}`;
}

export function OrderHistorySheet( { open, onOpenChange, guessNo, event, guess }: OrderHistorySheetProps ) {
    const [ expandedIndex, setExpandedIndex ] = useState<number | null>( null );
    const [ confirmDialogOpen, setConfirmDialogOpen ] = useState( false );
    const [ pendingCancelOrder, setPendingCancelOrder ] = useState<OrderBookLevel | null>( null );
    const queryClient = useQueryClient();
    const smartRefresh = useUserInfo( ( state ) => state.smartRefresh );

    // Fetch order book data
    const { data: orderBookData, isLoading, refetch } = useQuery<OrderBookData>( {
        queryKey: [ "orderBook", guessNo ],
        queryFn: async () => {
            const [ isOk, res ] = await apiGetUserGuessOrder( { guessNo } );
            if ( isOk ) {
                return get( res, "data", {} ) as OrderBookData;
            }
            return {} as OrderBookData;
        },
        enabled: open && !!guessNo,
    } );

    const orders = get( orderBookData, "orders", [] ) as OrderBookLevel[];

    // Mutation for canceling orders
    const cancelOrderMutation = useMutation( {
        mutationFn: ( params: { orderNo: string; orderNoType: 1 | 2 } ) => apiCancelOrder( params ),
    } );

    const handleToggleExpand = ( index: number ) => {
        setExpandedIndex( expandedIndex === index ? null : index );
    };

    const handleCloseOrderClick = ( order: OrderBookLevel ) => {
        setPendingCancelOrder( order );
        setConfirmDialogOpen( true );
    };

    const handleConfirmCancel = async () => {
        if ( !pendingCancelOrder ) return;

        try {
            // Note: Since API returns aggregated data without individual orderNo,
            // we would need to call a different API to get individual orders.
            // For now, we'll use guessNo with orderNoType 1 to cancel all orders at this price level
            const result = await cancelOrderMutation.mutateAsync( { orderNo: guessNo, orderNoType: 1 } );

            const [ isOk, response ] = result;
            if ( isOk ) {
                // Refresh queries
                await queryClient.invalidateQueries( { queryKey: [ "orderBook", guessNo ] } );
                await queryClient.invalidateQueries( { queryKey: [ "portfolio", "resting" ] } );
                // Actively refetch data to ensure immediate update
                await refetch();
                smartRefresh( RefreshReason.ORDER_CANCELED );

                // Show custom success toast
                const orderTypeLabel = getOrderTypeLabel(
                    get( pendingCancelOrder, "direction", 1 ),
                    get( pendingCancelOrder, "betType", 1 ),
                );
                const eventTitle = get( event, "title", "" ) || get( event, "eventTitle", "" );
                const guessTitle = get( guess, "title", "" ) || get( orderBookData, "title", "" );

                toast.custom( ( t ) => (
                    <div className="p-3 bg-surface-card rounded-lg shadow-lg min-w-[300px]">
                        {/* First row: icon + Order Cancelled + close button */}
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <SuccessIcon />
                                <span className="text-sm font-semibold text-t1">Order cancelled</span>
                            </div>
                            <button onClick={() => toast.dismiss( t )} className="text-t1 transition-colors">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                >
                                    <path
                                        d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </button>
                        </div>
                        {/* Second row: text content */}
                        <div className="text-xs text-t3">
                            <div>{eventTitle}</div>
                            <div>{orderTypeLabel} · {guessTitle}</div>
                        </div>
                    </div>
                ), { duration: 3000 } );

                setConfirmDialogOpen( false );
                setPendingCancelOrder( null );
            } else {
                toast.error( get( response, "msg", "Failed to cancel order" ) );
            }
        } catch ( error ) {
            console.error( "Cancel order error:", error );
            toast.error( "Failed to cancel order" );
        }
    };

    const getOrderTypeLabel = ( direction: number, betType: number ) => {
        const isBuy = direction === 1;
        const isYes = betType === 1;

        if ( isBuy && isYes ) return "Buy Yes";
        if ( isBuy && !isYes ) return "Buy No";
        if ( !isBuy && isYes ) return "Sell Yes";
        return "Sell No";
    };

    const getOrderTypeColor = ( direction: number, betType: number ) => {
        const isYes = betType === 1;
        // All Yes use green, all No use red, regardless of Buy/Sell
        return isYes ? "text-yes" : "text-no";
    };

    // Success icon component for toast
    const SuccessIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clipPath="url(#clip0_9842_27899)">
                <rect width="16" height="16" fill="white" />
                <path
                    d="M7.99998 14.6668C9.84091 14.6668 11.5076 13.9206 12.714 12.7142C13.9204 11.5078 14.6666 9.8411 14.6666 8.00016C14.6666 6.15923 13.9204 4.49256 12.714 3.28612C11.5076 2.07969 9.84091 1.3335 7.99998 1.3335C6.15905 1.3335 4.49238 2.07969 3.28593 3.28612C2.07951 4.49256 1.33331 6.15923 1.33331 8.00016C1.33331 9.8411 2.07951 11.5078 3.28593 12.7142C4.49238 13.9206 6.15905 14.6668 7.99998 14.6668Z"
                    fill="#09C285"
                    stroke="#09C285"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                />
                <path
                    d="M5.33331 8L7.33331 10L11.3333 6"
                    stroke="white"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </g>
            <defs>
                <clipPath id="clip0_9842_27899">
                    <rect width="16" height="16" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                showCloseButton={false}
                className={cn( "rounded-t-3xl p-0 max-w-[500px] mx-auto h-[80vh]" )}
            >
                <div className="overflow-hidden h-full flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between h-12 px-4 flex-shrink-0">
                        <SheetClose
                            onClick={() => onOpenChange( false )}
                            className="flex items-center justify-center size-16 rounded-lg transition-colors outline-0"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                className="size-6"
                            >
                                <path
                                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                                    fill="black"
                                    fillOpacity="0.9"
                                />
                            </svg>
                        </SheetClose>
                        <SheetTitle className="absolute left-1/2 -translate-x-1/2 text-base font-medium text-[rgba(0,0,0,0.90)]">
                            Resting orders
                        </SheetTitle>
                        {/* Trash icon - hidden for now */}
                        <button className="hidden items-center justify-center size-16 active:bg-surface-muted rounded-lg transition-colors">
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="size-6"
                            >
                                <path
                                    d="M7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7ZM17 6H7V19H17V6ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z"
                                    fill="var(--ui-state-danger)"
                                />
                            </svg>
                        </button>
                        <div className="w-16" />
                    </div>

                    <div className="px-4 pb-3 flex flex-col flex-1 min-h-0">
                        {/* Event Info */}
                        <div className="flex items-center gap-2 py-3 border-b border-stroke flex-shrink-0">
                            {get( orderBookData, "coverImageUrl", false ) && (
                                <SmartImage
                                    src={get( orderBookData, "coverImageUrl", "" )}
                                    alt={get( orderBookData, "title", "" )}
                                    className="size-10 rounded-lg object-cover"
                                />
                            )}
                            <div className="flex-1 flex flex-col gap-1">
                                {/* Event title */}
                                <h3 className="text-sm font-normal text-t1 line-clamp-1">
                                    {get( event, "title", "" ) || get( event, "eventTitle", "" )}
                                </h3>
                                {/* Guess title */}
                                <h4 className="text-sm font-semibold text-t1 line-clamp-1">
                                    {get( orderBookData, "title", "" ) || get( guess, "title", "" )}
                                </h4>
                            </div>
                        </div>

                        {/* Orders List */}
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {isLoading && (
                                <div className="flex items-center justify-center py-10">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-content-primary" />
                                </div>
                            )}

                            {!isLoading && orders.length === 0 && <EmptyState text="No resting orders" />}

                            {!isLoading && orders.length > 0 && (
                                <div className="divide-y divide-black/7">
                                    {orders.map( ( order, index ) => {
                                        const isExpanded = expandedIndex === index;
                                        const orderTypeLabel = getOrderTypeLabel(
                                            get( order, "direction", 1 ),
                                            get( order, "betType", 1 ),
                                        );
                                        const orderTypeColor = getOrderTypeColor(
                                            get( order, "direction", 1 ),
                                            get( order, "betType", 1 ),
                                        );
                                        const totalQuantity = get( order, "totalQuantity", 0 );
                                        const totalDealtQuantity = get( order, "totalDealtQuantity", 0 );

                                        return (
                                            <div key={index} className="bg-surface-card">
                                                {/* Collapsed view */}
                                                <button
                                                    onClick={() => handleToggleExpand( index )}
                                                    className="w-full flex items-center justify-between py-3 hover:bg-content-primary/[0.02] transition-colors"
                                                >
                                                    {/* Left: Order type label (no background) */}
                                                    <div className={cn( "text-sm font-medium", orderTypeColor )}>
                                                        {orderTypeLabel}
                                                    </div>
                                                    {/* Right: Quantity, expiration, and arrow */}
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex flex-col items-end gap-1">
                                                            <span className="text-sm font-medium text-t1">
                                                                {totalDealtQuantity} / {totalQuantity}
                                                            </span>
                                                            <span className="text-xs text-t3">
                                                                Expiration: Until Cancelled
                                                            </span>
                                                        </div>
                                                        {isExpanded
                                                            ? <ChevronUp className="w-8 h-8 text-t3 flex-shrink-0" />
                                                            : <ChevronDown className="w-8 h-8 text-t3 flex-shrink-0" />}
                                                    </div>
                                                </button>

                                                {/* Expanded view with smooth animation */}
                                                <AnimatePresence initial={false}>
                                                    {isExpanded && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: "auto", opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{
                                                                height: { duration: 0.3, ease: "easeInOut" },
                                                                opacity: { duration: 0.2, ease: "easeInOut" },
                                                            }}
                                                            style={{ overflow: "hidden" }}
                                                        >
                                                            <div className="pb-4 space-y-2">
                                                                {/* Cost */}
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-t3">Cost</span>
                                                                    <span className="text-xs font-medium text-t1">
                                                                        {formatMonetaryValue( order.totalAmount, {
                                                                            useScale: false,
                                                                            decimalDisplay: "max",
                                                                            maxPrecision: 4,
                                                                        } )}
                                                                    </span>
                                                                </div>

                                                                {/* Limit price */}
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-t3">Limit price</span>
                                                                    <span className="text-xs font-medium text-t1">
                                                                        {formatUnitPrice( order.price, {
                                                                            precision: 1,
                                                                            useScale: false,
                                                                        } )}
                                                                    </span>
                                                                </div>

                                                                {/* Market value */}
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-t3">
                                                                        Market value
                                                                    </span>
                                                                    <span className="text-xs font-medium text-t1">
                                                                        {formatUnitPrice( order.marketPrice, {
                                                                            precision: 1,
                                                                            useScale: false,
                                                                        } )}
                                                                    </span>
                                                                </div>

                                                                {/* Fees */}
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-t3">Fees</span>
                                                                    <span className="text-xs font-medium text-t1">
                                                                        {formatMonetaryValue( order.totalFeeAmount, {
                                                                            useScale: false,
                                                                            decimalDisplay: "max",
                                                                            maxPrecision: 4,
                                                                        } )}
                                                                    </span>
                                                                </div>

                                                                {/* Placed */}
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-t3">Placed</span>
                                                                    <span className="text-xs font-medium text-t1">
                                                                        {formatDate( get( order, "lastOrderTime", 0 ) )}
                                                                    </span>
                                                                </div>

                                                                {/* Close Order Button */}
                                                                <button
                                                                    onClick={() => handleCloseOrderClick( order )}
                                                                    className="hidden w-full h-11 mt-2 text-sm font-semibold text-no bg-surface-card border-2 border-[#D91616] rounded-lg hover:bg-state-danger-surface active:scale-[0.98] transition-all"
                                                                >
                                                                    Close Order
                                                                </button>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        );
                                    } )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </SheetContent>

            <ConfirmDialog
                open={confirmDialogOpen}
                onOpenChange={( open ) => {
                    setConfirmDialogOpen( open );
                    if ( !open ) {
                        setPendingCancelOrder( null );
                    }
                }}
                title="Are you sure?"
                description="Are you sure you want to cancel this open order?"
                confirmText="Confirm"
                cancelText="Never mind"
                onConfirm={handleConfirmCancel}
                isLoading={cancelOrderMutation.isPending}
            />
        </Sheet>
    );
}
